import React, { forwardRef, useState, Fragment } from 'react';
import { Stack } from '../../../atoms/stack/Stack';
import { Media } from '../media/Media';
import { Typography } from '../../../atoms/typography/Typography';
import { LinkButton } from '../../../atoms/linkButton/LinkButton';
import { Row } from '../../../atoms/row/Row';
import { Desktop } from '../../../../../static/icons/desktop';
import { Contact } from '../../../../../static/icons/contact';
import { Email } from '../../../../../static/icons/email';
import { IOS } from '../../../../../static/icons/ios';
import { Android } from '../../../../../static/icons/android';
import { Phone } from '../../../../../static/icons/phone';
import { LocationMarker } from '../../../../../static/icons/locationMarker';
import colors from '../../../../../tailwind.colors';
import { Tooltip } from '../../../atoms/tooltip/Tooltip';
import { StyledHtmlDiv } from '../../../atoms/styledHtmlDiv/StyledHtmlDiv';
import { ArrowFull } from '../../../../../static/icons/arrowFull';

const InfoItem = ({ item, t }) => (
  <Row>
    <Stack className="gap-0 justify-center">
      <Typography fontVariant="md" textAlign="left" className="font-bold">
        {item.title}
      </Typography>
      <Typography
        fontVariant="sm"
        textAlign="left"
        className="!text-customGray-500 font-normal"
      >
        {item.description}
      </Typography>
    </Stack>
    <Stack className="flex-1 justify-center">
      {item.type === 'text' && <TextOption value={item.value} />}
      {item.type === 'link' && <LinkOption value={item.value} />}
      {item.type === 'boolean' && <BooleanOptions value={item.value} t={t} />}
      {item.type === 'textOptions' && (
        <TextOptionColor value={item.value} t={t} />
      )}
      {item.type === 'options' && <CountryOptions value={item.value} t={t} />}
      {item.type === 'iconsSupport' && (
        <IconSupportOptions value={item.value} t={t} />
      )}
      {item.type === 'iconsMobile' && (
        <IconMobileOptions value={item.value} t={t} />
      )}
      {item.type === 'iconsEducation' && (
        <IconEducationOptions value={item.value} t={t} />
      )}
    </Stack>
  </Row>
);

export const BooleanOptions = ({ value, t }) => (
  <Row className="gap-5 h-full items-center justify-end">
    <Typography
      fontVariant="md"
      className={`font-bold ${
        value ? '!text-primary-700' : '!text-customGray-300'
      }`}
    >
      {t('spQf.yes')}
    </Typography>
    <Typography
      fontVariant="md"
      className={`font-bold ${
        !value ? '!text-primary-700' : '!text-customGray-300'
      }`}
    >
      {t('spQf.no')}
    </Typography>
  </Row>
);

const LinkOption = ({ value }) =>
  value?.map((item) => (
    <LinkButton
      key={item.id}
      title={item.name}
      label={item.name}
      variant="transparent"
      href={item.url}
      className="text-clamp-md w-fit self-end px-0 font-normal underline !text-primary-700"
    />
  ));

export const TextOption = ({ value }) => (
  <Typography fontVariant="md" textAlign="right" className="font-normal">
    {value}
  </Typography>
);

export const TextOptionColor = ({ value }) => (
  <Typography
    fontVariant="md"
    textAlign="right"
    className="font-bold cursor-default !text-primary-700"
  >
    {value}
  </Typography>
);

export const CountryOptions = ({ value, t }) => (
  <Row className="gap-5 h-full items-center justify-end">
    {value
      .filter((option) =>
        ['SV', 'SE', 'NO', 'DA', 'DK', 'FI', 'EN', 'Global'].includes(
          option.text
        )
      )
      .map((option) => {
        if (!option.hoverText)
          return (
            <Typography
              key={option.text}
              fontVariant="md"
              className="font-bold cursor-default !text-primary-700"
            >
              {option.text}
            </Typography>
          );
        return (
          <Tooltip
            key={option.text}
            description={t(option.hoverText)}
            direction={option?.direction || 'top'}
          >
            <Typography
              fontVariant="md"
              className="font-bold cursor-default !text-primary-700"
            >
              {option.text}
            </Typography>
          </Tooltip>
        );
      })}
  </Row>
);

export const IconSupportOptions = ({ value, t }) => (
  <Row className="gap-5 h-full items-center justify-end">
    <Tooltip description={t('guide.support.chat')}>
      <Desktop color={value[0] ? undefined : colors.customGray[300]} />
    </Tooltip>
    <Tooltip description={t('guide.support.phone')}>
      <Contact color={value[1] ? undefined : colors.customGray[300]} />
    </Tooltip>
    <Tooltip description={t('guide.support.email')}>
      <Email color={value[2] ? undefined : colors.customGray[300]} />
    </Tooltip>
  </Row>
);

const IconMobileOptions = ({ value, t }) => (
  <Row className="gap-5 h-full items-center justify-end">
    <Tooltip description="iOS">
      <IOS color={value[0] ? undefined : colors.customGray[300]} />
    </Tooltip>
    <Tooltip description="Android">
      <Android color={value[1] ? undefined : colors.customGray[300]} />
    </Tooltip>
    <Tooltip description={t('spQf.mobileApp')}>
      <Phone color={value[2] ? undefined : colors.customGray[300]} />
    </Tooltip>
  </Row>
);

const IconEducationOptions = ({ value, t }) => (
  <Row className="gap-5 h-full items-center justify-end">
    <Tooltip description={t('spQf.educationOnline')}>
      <Desktop color={value[0] ? undefined : colors.customGray[300]} />
    </Tooltip>
    <Tooltip description={t('spQf.educationContact')}>
      <Contact color={value[1] ? undefined : colors.customGray[300]} />
    </Tooltip>
    <Tooltip description={t('spQf.educationLocation')}>
      <LocationMarker color={value[2] ? undefined : colors.customGray[300]} />
    </Tooltip>
  </Row>
);

const InfoSection = forwardRef(
  ({ section, isPaid, t, handleDataLayer, openModalForm }, ref) => {
    const data = isPaid
      ? section.componentData
      : section.componentData.filter((el) => el.unpaid === true);

    // const hasPitchAsset = section.pitchAsset.length >= 1;
    const [expanded, setExpanded] = useState(
      section.textSections.length <= 700
    );

    const toggleExpand = () => {
      setExpanded(!expanded);
    };

    return (
      <div
        ref={ref}
        className="flex w-full flex-col flex-1 px-8 lg:mx-auto max-w-7xl"
      >
        <div className="flex flex-col md:flex-row flex-1 justify-between gap-10  md:gap-3 py-5">
          <Stack className="flex-[0.5] h-auto max-w-md">
            <Stack
              className={`flex-1 relative ${
                expanded ? 'max-h-full' : 'max-h-[400px] overflow-hidden'
              }`}
            >
              {section.textSections.texts.map((textSection, i) => (
                <Fragment key={textSection.id}>
                  <Typography as="h2" fontVariant="h4" textAlign="left">
                    {textSection.title}
                  </Typography>
                  {isPaid && i === 0 && section.pitchAsset.length >= 1 && (
                    <div className="self-start">
                      <Media
                        category={section.category}
                        media={section.pitchAsset}
                        systemName={section.systemName}
                        handleDataLayer={handleDataLayer}
                      />
                    </div>
                  )}
                  <StyledHtmlDiv
                    className="text-clamp-lg font-light normal-case text-left"
                    content={textSection.text}
                  />
                  {!expanded && (
                    <div className="bg-gradient-to-t from-primary-50 to-transparent absolute bottom-0 top-24 right-0 left-0" />
                  )}
                </Fragment>
              ))}
            </Stack>
            {!expanded && (
              <Row className="flex items-center gap-2 global-hover w-fit">
                <button
                  type="button"
                  onClick={toggleExpand}
                  className="flex-row flex items-center gap-2 global-hover w-fit text-clamp-h8 font-medium normal-case text-left !text-primary-700"
                >
                  {t('common.readMore')}
                  <ArrowFull color={colors.primary[700]} direction="right" />
                </button>
              </Row>
            )}
            {section.showForm && (
              <Stack className="min-w-60 justify-center gap-2 mt-10">
                <LinkButton
                  textTransform="uppercase"
                  variant="primary-filled"
                  onClick={openModalForm}
                  label={section.form.btn}
                  size="xl"
                  className="max-w-fit"
                  // className="w-60 text-clamp-sm border border-primary-700"
                />
                {/* <LinkButton
                variant="transparent"
                size="lg"
                className="text-clamp-sm w-60 border border-customGray-300"
                label="TODO: BLI KONTAKTAD"
              /> */}
              </Stack>
            )}
          </Stack>
          <Stack className="flex-[0.5] h-auto gap-4">
            {data
              .filter(
                (item) =>
                  !(Array.isArray(item.value) && item.value.length === 0) &&
                  item.value !== null
              )
              .map((item) => (
                <InfoItem key={item.title} item={item} t={t} />
              ))}
          </Stack>
        </div>
      </div>
    );
  }
);

export default InfoSection;
